/* ------------------------------------------------------------------
   Motion layer — central GSAP + Lenis controller.
   All new scroll-driven / reveal / cursor motion is initialised here.
   Nothing outside this file should call gsap.* or Lenis directly.

   Namespacing: everything new uses the `mo` prefix (classes, data
   attributes, custom events) so it can never collide with existing
   theme selectors or scripts.

   Existing animation (marquee, moving-squares, breathing hero,
   volume-slider thumb, product slider, Tailwind hover transitions) is
   untouched and out of scope for this file — see .claude/rules and the
   Phase 0 audit. This module must never select or mutate those
   elements.
   ------------------------------------------------------------------ */
(function () {
  if (!window.gsap || !window.ScrollTrigger || !window.Lenis) return;

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  var Lenis = window.Lenis;
  gsap.registerPlugin(ScrollTrigger);

  /* Never run the smooth-scroll / motion layer on checkout or customer
     account pages. data-mo-page-type is set from request.page_type in
     layout/theme.liquid (Shopify's own page-type string, e.g.
     "customers/account", "customers/login"). */
  var PAGE_TYPE = document.body.getAttribute('data-mo-page-type') || '';
  var isCheckout = /\/checkouts\//.test(window.location.pathname) || PAGE_TYPE === 'checkout';
  var isAccount = PAGE_TYPE.indexOf('customers/') === 0;
  if (isCheckout || isAccount) return;

  /* ------------------------------------------------------------------
     Tokens — durations / easing / stagger for all new motion.
     No existing CSS/JS timing value was reusable as a UI-interaction
     easing: the theme's only precedent (volume-slider thumb, 120ms
     ease-out) is a micro hover transition, and everything else
     (marquee, moving-squares, breathing hero) is a long ambient loop,
     not an entrance-reveal feel. These are standard, conservative
     values — flagged for review rather than invented silently.
     ------------------------------------------------------------------ */
  var TOKENS = {
    durationReveal: 0.6,
    durationFade: 0.4,
    ease: 'power2.out',
    stagger: 0.08,
    revealDistance: 24, // px, within the 20-30px spec range
    lenisLerp: 0.1
  };
  window.moTokens = TOKENS;

  var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var lenis = null;
  var lenisRafHandler = null;

  function startLenis() {
    if (lenis || reducedMotionQuery.matches) return;
    lenis = new Lenis({ lerp: TOKENS.lenisLerp });
    lenis.on('scroll', ScrollTrigger.update);
    lenisRafHandler = function (time) {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(lenisRafHandler);
    gsap.ticker.lagSmoothing(0);
    window.moLenis = lenis;
  }

  function stopAndDestroyLenis() {
    if (!lenis) return;
    if (lenisRafHandler) gsap.ticker.remove(lenisRafHandler);
    lenis.destroy();
    lenis = null;
    lenisRafHandler = null;
    window.moLenis = null;
  }

  /* Public helper for content that toggles display:none/hidden on
     data-mo-reveal elements outside of scroll (e.g. shop-page-filter.js
     unhiding a filtered card). A card hidden at the moment its
     ScrollTrigger would have fired never gets a scroll event once
     re-shown, so it would stay stuck at the reveal's hidden state
     (opacity:0) — this forces it to its finished state immediately
     instead of waiting for a scroll that may never come. */
  window.moRevealNow = function (root) {
    var scope = root || document;
    scope.querySelectorAll('[data-mo-reveal]').forEach(function (el) {
      if (!el.classList.contains('hidden') && el.offsetParent !== null) {
        gsap.set(el, { opacity: 1, y: 0 });
      }
    });
  };

  /* The nav is position:fixed, so it covers the same strip of the
     viewport at every scroll position — any in-page jump has to land
     its target that much further up or the target's first navHeight
     pixels end up hidden behind the bar. Measured live rather than
     recomputed from the Nav_Height vh setting, so it tracks whatever
     the merchant configures (border widths included) and stays correct
     across viewport resizes, which change a vh-based height. */
  function getNavHeight() {
    var navEl = document.querySelector('.NavBar');
    return navEl ? navEl.getBoundingClientRect().height : 0;
  }

  /* Public helper for anchor links / in-page jumps so they still work
     with Lenis virtualising scroll (see shop-page-filter.js). Offsets
     for the fixed nav by default; pass { offset: 0 } to opt out. */
  window.moScrollTo = function (target, opts) {
    var options = {};
    var key;
    for (key in opts) { if (Object.prototype.hasOwnProperty.call(opts, key)) options[key] = opts[key]; }
    if (options.offset == null) options.offset = -getNavHeight();
    // Lenis silently no-ops scrollTo() whenever it is stopped unless
    // force is passed — and the homepage snap stepper deliberately keeps
    // it stopped while in its zone. Without this, an in-page jump fired
    // from a stopped state would do nothing at all.
    if (options.force == null) options.force = true;

    if (lenis) {
      lenis.scrollTo(target, options);
    } else {
      var el = typeof target === 'string' ? document.querySelector(target) : target;
      if (!el) return;
      if (reducedMotionQuery.matches) {
        window.scrollTo(0, el.getBoundingClientRect().top + window.scrollY + options.offset);
      } else {
        window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY + options.offset, behavior: 'smooth' });
      }
    }
  };

  /* Overlays (mobile nav, future drawers/modals) call these instead of
     touching Lenis directly, so behaviour is correct whether Lenis is
     currently running or was disabled by reduced-motion. */
  window.moLockScroll = function () {
    if (lenis) lenis.stop();
  };
  window.moUnlockScroll = function () {
    if (lenis) lenis.start();
  };

  /* ------------------------------------------------------------------
     Reveal system — opt-in only via data-mo-reveal. Nothing animates
     unless a section/element explicitly requests it. Triggers are
     tracked per section id so the theme editor's section lifecycle
     (load/unload/reorder) never leaves orphaned or duplicated triggers.
     ------------------------------------------------------------------ */
  var sectionTriggers = {}; // sectionId -> [ScrollTrigger, ...]

  function killTriggersFor(sectionId) {
    var list = sectionTriggers[sectionId];
    if (!list) return;
    list.forEach(function (st) { st.kill(); });
    delete sectionTriggers[sectionId];
  }

  /* Shopify wraps every rendered section in <div id="shopify-section-{id}">
     automatically (both {% section %} and {% sections %} group output) —
     a stable hook that needs no changes to any section file. */
  function getShopifySectionId(el) {
    var wrapper = el.closest ? el.closest('[id^="shopify-section-"]') : null;
    return wrapper ? wrapper.id.replace('shopify-section-', '') : 'global';
  }

  function initReveals(root) {
    var els = root.querySelectorAll('[data-mo-reveal]');
    if (!els.length) return;

    /* Group by containing section so triggers can be killed per-section
       on shopify:section:unload without affecting other sections, even
       when root is document (initial boot covers every section at once). */
    var bySection = {};
    els.forEach(function (el) {
      var sectionId = getShopifySectionId(el);
      if (!bySection[sectionId]) bySection[sectionId] = [];
      bySection[sectionId].push(el);
    });

    Object.keys(bySection).forEach(function (sectionId) {
      var sectionEls = bySection[sectionId];

      var mm = gsap.matchMedia();
      mm.add(
        { reduced: '(prefers-reduced-motion: reduce)', normal: '(prefers-reduced-motion: no-preference)' },
        function (ctx) {
          var reduced = ctx.conditions.reduced;
          var created = [];

          sectionEls.forEach(function (el, i) {
            if (reduced) {
              gsap.set(el, { opacity: 1, y: 0 });
              return;
            }

            // Anything already on screen when this runs has already
            // painted at full opacity — the browser rendered the HTML
            // well before this deferred script executed. Hiding it now
            // to animate it back in reads as the content loading twice
            // (clearly visible on the shop page, where a screenful of
            // product cards would blink out and fade back). Leave those
            // as they are and only arm the reveal for elements still
            // below the fold.
            if (el.getBoundingClientRect().top < window.innerHeight) {
              gsap.set(el, { opacity: 1, y: 0 });
              return;
            }

            gsap.set(el, { opacity: 0, y: TOKENS.revealDistance });

            var groupAttr = el.getAttribute('data-mo-reveal-group');
            var delay = groupAttr ? (parseInt(el.getAttribute('data-mo-reveal-index') || i, 10) * TOKENS.stagger) : 0;

            var st = ScrollTrigger.create({
              trigger: el,
              start: 'top 85%',
              once: true,
              onEnter: function () {
                gsap.to(el, {
                  opacity: 1,
                  y: 0,
                  duration: TOKENS.durationReveal,
                  ease: TOKENS.ease,
                  delay: delay
                });
              }
            });
            created.push(st);
          });

          if (!sectionTriggers[sectionId]) sectionTriggers[sectionId] = [];
          sectionTriggers[sectionId] = sectionTriggers[sectionId].concat(created);

          return function () {
            created.forEach(function (st) { st.kill(); });
          };
        }
      );
    });
  }

  /* ------------------------------------------------------------------
     PDP gallery crossfade — layers an opacity dip/rise on top of the
     existing slide transform (product-page-content.js owns the
     transform entirely; unchanged). Listens for the additive
     'mo:slide-change' custom event that file now dispatches after each
     slide/variant image change. Slide ownership: transform belongs to
     product-page-content.js, opacity belongs here — no double-driving
     of the same property.
     ------------------------------------------------------------------ */
  function initGalleryCrossfade(root) {
    var slider = root.querySelector('[data-slider]');
    if (!slider) return;

    slider.addEventListener('mo:slide-change', function (e) {
      if (reducedMotionQuery.matches) return;
      var index = e.detail && e.detail.index;
      var target = slider.children[index];
      if (!target) return;

      var img = target.querySelector('img');

      function fadeIn() {
        gsap.fromTo(target, { opacity: 0.4 }, { opacity: 1, duration: TOKENS.durationFade, ease: TOKENS.ease });
      }

      if (img && !img.complete) {
        gsap.set(target, { opacity: 0.4 });
        if (img.decode) {
          img.decode().then(fadeIn).catch(fadeIn);
        } else {
          img.addEventListener('load', fadeIn, { once: true });
        }
      } else {
        fadeIn();
      }
    });
  }

  /* ------------------------------------------------------------------
     Custom cursor — GSAP-driven replacement for the previous rAF/lerp
     implementation in cursor.js. Same element (#custom-cursor), same
     settings-driven CSS custom properties, same activation rules
     (desktop + fine/hover pointer), same reduced-motion accommodation
     (snap instead of trailing ease). cursor.js's <script> tag has been
     removed from theme.liquid — this is now the sole driver of
     #custom-cursor's transform.
     ------------------------------------------------------------------ */
  function initCursor() {
    var cursorEl = document.getElementById('custom-cursor');
    if (!cursorEl) return;

    var desktopQuery = window.matchMedia('(min-width: 1024px)');
    var fineHoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    var xTo, yTo;
    var active = false;
    var hasPosition = false;

    /* Theme settings → Mouse pointer → Circle responsiveness. Expressed
       as a percentage for the merchant (higher = snappier) and mapped
       here onto the follow tween's duration: 100% lands on 0s, so the
       circle sits exactly on the pointer, while the minimum 10% gives a
       0.54s trail. The previous behaviour was a hardcoded 0.5s, which is
       what made the circle feel laggy. */
    function followDuration() {
      var pct = parseFloat(cursorEl.getAttribute('data-cursor-speed'));
      if (isNaN(pct)) pct = 70;
      pct = Math.min(100, Math.max(10, pct));
      return (100 - pct) / 100 * 0.6;
    }

    function buildTweens(instant) {
      var dur = instant ? 0 : followDuration();
      xTo = gsap.quickTo(cursorEl, 'x', { duration: dur, ease: 'power3' });
      yTo = gsap.quickTo(cursorEl, 'y', { duration: dur, ease: 'power3' });
    }

    /* Sections that opt in (Theme editor → section → "Change cursor
       colour over this section") carry data-cursor-hover with the hover
       colour for their background, resolved from the brand palette in
       Liquid. Walking up from the hovered element means nested opted-in
       sections resolve to the innermost one, which is what a merchant
       would expect. Colour is tweened rather than snapped so crossing a
       boundary reads as a blend rather than a flicker. */
    var baseColor = null;
    var currentHover = null;

    function hoverColorAt(target) {
      var el = target && target.closest ? target.closest('[data-cursor-hover]') : null;
      return el ? el.getAttribute('data-cursor-hover') : null;
    }

    function applyHoverColor(next) {
      if (next === currentHover) return;
      currentHover = next;
      if (baseColor === null) {
        baseColor = getComputedStyle(cursorEl).backgroundColor;
      }
      gsap.to(cursorEl, {
        backgroundColor: next || baseColor,
        duration: TOKENS.durationFade,
        ease: TOKENS.ease,
        overwrite: 'auto'
      });
    }

    function onMouseMove(e) {
      if (!hasPosition) {
        gsap.set(cursorEl, { x: e.clientX, y: e.clientY, xPercent: -50, yPercent: -50 });
        hasPosition = true;
      }
      xTo(e.clientX);
      yTo(e.clientY);
      applyHoverColor(hoverColorAt(e.target));
    }

    function activate() {
      if (active) return;
      active = true;
      hasPosition = false;
      buildTweens(reducedMotionQuery.matches);
      document.documentElement.classList.add('custom-cursor-active');
      document.addEventListener('mousemove', onMouseMove);
    }

    function deactivate() {
      if (!active) return;
      active = false;
      document.documentElement.classList.remove('custom-cursor-active');
      document.removeEventListener('mousemove', onMouseMove);
      // Drop any hover tint so re-activating starts from the base colour
      // rather than whatever section the pointer happened to leave on.
      if (currentHover !== null) {
        currentHover = null;
        if (baseColor !== null) gsap.set(cursorEl, { backgroundColor: baseColor });
      }
    }

    function sync() {
      if (desktopQuery.matches && fineHoverQuery.matches) activate();
      else deactivate();
    }

    function onReducedChange() {
      if (active) buildTweens(reducedMotionQuery.matches);
    }

    /* The cursor lives in layout/theme.liquid, not a section, so editing
       its settings never fires shopify:section:load for it and the tweens
       would keep the old duration until a full reload. Rebuilding them on
       any section load is enough for the merchant to see responsiveness
       changes immediately, and cheap — quickTo just replaces two tweens,
       no listeners are re-bound. */
    window.moRefreshCursor = function () {
      if (active) buildTweens(reducedMotionQuery.matches);
    };

    sync();
    desktopQuery.addEventListener('change', sync);
    fineHoverQuery.addEventListener('change', sync);
    reducedMotionQuery.addEventListener('change', onReducedChange);
  }

  /* ------------------------------------------------------------------
     Lifecycle
     ------------------------------------------------------------------ */
  function initGalleryCrossfades(root) {
    root.querySelectorAll('[data-product-section]').forEach(initGalleryCrossfade);
  }

  /* ------------------------------------------------------------------
     Drifting images — a decorative picture crosses the screen every so
     often: random image, random side, random entry height, random exit
     height, so no two crossings look alike.

     Deliberately NOT tied to scroll. It runs on its own clock, so the
     motion continues whether or not the visitor is scrolling.

     Layout is untouched by this: the image is position:absolute inside a
     fixed, full-viewport, pointer-events-none layer, and only transform
     and visibility are animated. Nothing here can reflow the page or
     intercept a click.
     ------------------------------------------------------------------ */
  function initDrift() {
    var layer = document.getElementById('drift-layer');
    if (!layer) return;

    var items = Array.prototype.slice.call(
      layer.querySelectorAll('[data-drift-item]')
    );
    if (!items.length) return;

    var mobileQuery = window.matchMedia('(max-width: 1023px)');
    var timer = null;
    var tween = null;
    var current = null;

    function num(attr, fallback) {
      var v = parseFloat(layer.getAttribute(attr));
      return isNaN(v) ? fallback : v;
    }

    function rand(min, max) {
      return min + Math.random() * (max - min);
    }

    /* Shuffled bag, not an independent pick per crossing: picking at random
       from three images repeats the previous one a third of the time, which
       reads as "it keeps showing the same picture". A bag plays each image
       once before any repeats. */
    var bag = [];
    var lastEl = null;

    function refillBag() {
      bag = items.slice();
      // Fisher-Yates.
      for (var i = bag.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var tmp = bag[i];
        bag[i] = bag[j];
        bag[j] = tmp;
      }
      /* If the reshuffle puts the image we just showed at the front, it
         would still appear twice in a row across the bag boundary. Swap it
         deeper in. */
      if (bag.length > 1 && bag[0] === lastEl) {
        var k = 1 + Math.floor(Math.random() * (bag.length - 1));
        bag[0] = bag[k];
        bag[k] = lastEl;
      }
    }

    function nextImage() {
      if (!bag.length) refillBag();
      var el = bag.shift();
      lastEl = el;
      return el;
    }

    /* Sides alternate rather than flipping a coin each time: independent
       flips produce runs of the same side often enough to look stuck. The
       occasional random break stops it becoming a metronome. */
    var lastFromLeft = Math.random() < 0.5;

    function nextSide() {
      lastFromLeft = Math.random() < 0.2 ? lastFromLeft : !lastFromLeft;
      return lastFromLeft;
    }

    function stop() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (tween) {
        tween.kill();
        tween = null;
      }
      if (current) {
        gsap.set(current, { visibility: 'hidden' });
        current = null;
      }
    }

    function crossOnce() {
      /* A constantly-moving element is exactly what prefers-reduced-motion
         is for, so the loop simply stops while that is set — and resumes
         via the listener below if it is turned off. */
      if (reducedMotionQuery.matches) return;

      var el = nextImage();
      var fromLeft = nextSide();

      /* A not-yet-decoded image measures 0, which would put the vertical
         bound in the wrong place and start the crossing on-screen rather
         than off it. Wait for it and take the next slot instead of
         animating something unmeasurable. */
      if (!el.complete || !el.naturalWidth) {
        var retry = function () {
          if (timer) clearTimeout(timer);
          timer = setTimeout(crossOnce, 200);
        };
        el.addEventListener('load', retry, { once: true });
        el.addEventListener('error', retry, { once: true });
        return;
      }

      /* Bounds are the merchant's "highest/lowest point" settings, as vh
         measured down from the top. Clamped and ordered here so a
         bottom-above-top misconfiguration still animates sanely rather
         than producing a negative range. */
      var topPct = Math.min(100, Math.max(0, num('data-drift-top', 15)));
      var botPct = Math.min(100, Math.max(0, num('data-drift-bottom', 85)));
      if (botPct < topPct) {
        var swap = topPct;
        topPct = botPct;
        botPct = swap;
      }

      var vh = window.innerHeight;
      var vw = window.innerWidth;
      /* Subtract the image's own height so the lowest point is where its
         BOTTOM sits, not its top — otherwise a large image set to 85vh
         hangs off the bottom of the screen. */
      var elH = el.offsetHeight || 0;
      var minY = topPct / 100 * vh;
      var maxY = Math.max(minY, botPct / 100 * vh - elH);

      /* Two independent draws land close together often, and a crossing
         that barely changes height reads as flat — the same path every
         time. The exit is therefore pushed at least a quarter of the band
         away from the entry, in whichever direction has room, so every
         crossing visibly rises or falls. */
      var startY = rand(minY, maxY);
      var band = maxY - minY;
      var minTravel = band * 0.25;
      var endY;

      if (band <= 0) {
        endY = startY;
      } else {
        var roomAbove = startY - minY;
        var roomBelow = maxY - startY;
        var goDown;
        if (roomBelow < minTravel) {
          goDown = false;
        } else if (roomAbove < minTravel) {
          goDown = true;
        } else {
          goDown = Math.random() < 0.5;
        }
        endY = goDown
          ? rand(startY + minTravel, maxY)
          : rand(minY, startY - minTravel);
      }

      /* Start and end fully off-screen on both sides, so the image is
         never seen appearing or vanishing mid-air. */
      var elW = el.offsetWidth || 0;
      var startX = fromLeft ? -elW : vw;
      var endX = fromLeft ? vw : -elW;

      var duration = mobileQuery.matches
        ? num('data-drift-duration-mobile', 40)
        : num('data-drift-duration', 25);

      current = el;
      gsap.set(el, { x: startX, y: startY, visibility: 'visible' });

      tween = gsap.to(el, {
        x: endX,
        y: endY,
        duration: duration,
        ease: 'none',
        onComplete: function () {
          gsap.set(el, { visibility: 'hidden' });
          current = null;
          tween = null;
          scheduleNext();
        }
      });
    }

    function scheduleNext() {
      if (timer) clearTimeout(timer);
      var gap = num('data-drift-gap', 20);
      timer = setTimeout(crossOnce, Math.max(0, gap) * 1000);
    }

    /* Pause while the tab is in the background: an animation nobody can see
       still costs battery, and GSAP would otherwise fast-forward the tween
       on return. */
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        stop();
      } else if (!reducedMotionQuery.matches) {
        scheduleNext();
      }
    });

    reducedMotionQuery.addEventListener('change', function () {
      stop();
      if (!reducedMotionQuery.matches) scheduleNext();
    });

    /* First crossing starts after a short settle, not a full gap: waiting
       the whole configured gap (20s by default) before anything appears at
       all makes the feature look broken on first load. Subsequent
       crossings use the real gap. */
    if (timer) clearTimeout(timer);
    timer = setTimeout(crossOnce, 1500);
  }

  function boot() {
    startLenis();
    initReveals(document);
    initCursor();
    initGalleryCrossfades(document);
    initDrift();
  }

  reducedMotionQuery.addEventListener('change', function () {
    if (reducedMotionQuery.matches) {
      stopAndDestroyLenis();
    } else {
      startLenis();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    ScrollTrigger.refresh();
    initReveals(e.target);
    initGalleryCrossfades(e.target);
    if (window.moRefreshCursor) window.moRefreshCursor();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var sectionId = e.target && e.target.id ? e.target.id.replace('shopify-section-', '') : null;
    if (sectionId) killTriggersFor(sectionId);
    ScrollTrigger.refresh();
  });
})();
